/**
 * Sector Question Wheels
 *
 * Generate question wheels from researched sector topics.
 * - Select a sector
 * - View topics with research completed
 * - Select topics to include in wheel
 * - Generate sector-level question wheel (12 questions per wheel)
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sparkles,
  Eye,
  Check,
  AlertCircle,
  RefreshCw,
  Loader2,
  CheckCircle2,
  MessageSquare,
  Trash2,
  Search,
  Filter,
  TrendingUp,
  Copy,
  Globe,
} from 'lucide-react';
import { authFetch, handleResponse } from '@/utils/auth-fetch';
import { useToast } from '@/components/ui/use-toast';

interface Sector {
  id: number;
  name: string;
  description?: string;
}

interface Topic {
  id: number;
  topic_name: string;
  topic_description: string;
  status: string;
  language: string;
  created_at: string;
  has_question_wheel: boolean;
}

interface QuestionWheel {
  id: string;
  name: string;
  description: string;
  language: string;
  wheel_type: string;
  question_count: number;
  status: string;
  created_at: string;
}

interface Question {
  id: string;
  question_text: string;
  question_type: string;
  intent_type: string;
  priority: number;
  target_persona: string;
}

export default function SectorQuestionWheels() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [enrichingWheelId, setEnrichingWheelId] = useState<string | null>(null);

  // Data
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [selectedSectorId, setSelectedSectorId] = useState<string>('');
  const [topics, setTopics] = useState<Topic[]>([]);
  const [selectedTopicIds, setSelectedTopicIds] = useState<number[]>([]);
  const [wheels, setWheels] = useState<QuestionWheel[]>([]);

  // Search and Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [onlyWithoutWheels, setOnlyWithoutWheels] = useState(false);

  // View Details Modal
  const [detailsModal, setDetailsModal] = useState<{
    open: boolean;
    wheel: QuestionWheel | null;
    questions: Question[];
    loading: boolean;
  }>({
    open: false,
    wheel: null,
    questions: [],
    loading: false,
  });

  useEffect(() => {
    loadSectors();
  }, []);

  useEffect(() => {
    if (selectedSectorId) {
      loadTopicsAndWheels();
    }
  }, [selectedSectorId, searchTerm, onlyWithoutWheels]);

  const loadSectors = async () => {
    try {
      setLoading(true);
      const response = await authFetch('/api/sectors');
      const data = await handleResponse<Sector[]>(response);
      setSectors(data);
    } catch (error) {
      console.error('Error loading sectors:', error);
      toast({
        title: 'Error',
        description: 'Failed to load sectors',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadTopicsAndWheels = async () => {
    if (!selectedSectorId) return;

    try {
      setLoading(true);

      // Build query parameters
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (onlyWithoutWheels) params.append('hasWheel', 'false');

      // Load topics with research completed
      const query = params.toString() ? `?${params.toString()}` : '';
      const topicsResponse = await authFetch(`/api/sectors/${selectedSectorId}/topics${query}`);
      const topicsData = await handleResponse<{ topics: Topic[] }>(topicsResponse);

      // Filter only completed topics (those with research)
      const completedTopics = topicsData.topics.filter(
        (t: Topic) => t.status === 'completed'
      );
      setTopics(completedTopics);

      // Load existing wheels
      const wheelsResponse = await authFetch(`/api/sectors/${selectedSectorId}/question-wheels`);
      const wheelsData = await handleResponse<{ wheels: QuestionWheel[] }>(wheelsResponse);
      setWheels(wheelsData.wheels);
    } catch (error) {
      console.error('Error loading topics and wheels:', error);
      toast({
        title: 'Error',
        description: 'Failed to load topics and wheels',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTopicToggle = (topicId: number) => {
    setSelectedTopicIds(prev =>
      prev.includes(topicId)
        ? prev.filter(id => id !== topicId)
        : [...prev, topicId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTopicIds.length === topics.length) {
      setSelectedTopicIds([]);
    } else {
      setSelectedTopicIds(topics.map(t => t.id));
    }
  };

  const handleGenerateWheel = async () => {
    if (selectedTopicIds.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one topic',
        variant: 'destructive',
      });
      return;
    }

    const totalTopics = selectedTopicIds.length;
    const confirmed = confirm(
      `This will create ${totalTopics} separate question wheels (one per topic), each with ~12 questions.\n\n` +
      `Total: ~${totalTopics * 12} questions to generate.\n\n` +
      `This may take several minutes. Continue?`
    );

    if (!confirmed) return;

    try {
      setGenerating(true);
      let successCount = 0;
      let failCount = 0;
      let totalQuestions = 0;
      const startTime = Date.now();

      // Generate one wheel per topic
      for (let i = 0; i < selectedTopicIds.length; i++) {
        const topicId = selectedTopicIds[i];

        try {
          // Update progress via toast
          toast({
            title: 'Generating...',
            description: `Processing topic ${i + 1}/${totalTopics}...`,
          });

          const response = await authFetch(`/api/sectors/${selectedSectorId}/generate-question-wheel`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              topicIds: [topicId], // Single topic per wheel
              language: 'es',
              wheelType: 'attribute',
              autoPublish: false,
            }),
          });

          const result = await handleResponse<{
            wheelId: string;
            wheelName: string;
            questionsGenerated: number;
            durationMs: number;
          }>(response);

          successCount++;
          totalQuestions += result.questionsGenerated;
        } catch (error: any) {
          console.error(`Error generating wheel for topic ${topicId}:`, error);
          failCount++;
        }
      }

      const totalTime = Math.round((Date.now() - startTime) / 1000);

      toast({
        title: 'Bulk Generation Complete',
        description: `✅ ${successCount} wheels created (${totalQuestions} questions)\n❌ ${failCount} failed\n⏱️ ${totalTime}s total`,
      });

      // Reload wheels and reset selection
      await loadTopicsAndWheels();
      setSelectedTopicIds([]);
    } catch (error: any) {
      console.error('Error during bulk generation:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate question wheels',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleViewWheel = async (wheel: QuestionWheel) => {
    setDetailsModal({
      open: true,
      wheel,
      questions: [],
      loading: true,
    });

    try {
      const response = await authFetch(`/api/sectors/${selectedSectorId}/question-wheels/${wheel.id}/questions`);
      const data = await handleResponse<{ questions: Question[] }>(response);

      setDetailsModal(prev => ({
        ...prev,
        questions: data.questions,
        loading: false,
      }));
    } catch (error) {
      console.error('Error loading wheel questions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load wheel questions',
        variant: 'destructive',
      });
      setDetailsModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteWheel = async (wheelId: string) => {
    if (!confirm('Are you sure you want to delete this question wheel? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await authFetch(`/api/sectors/${selectedSectorId}/question-wheels/${wheelId}`, {
        method: 'DELETE',
      });

      await handleResponse(response);

      toast({
        title: 'Success',
        description: 'Question wheel deleted successfully',
      });

      // Reload wheels
      await loadTopicsAndWheels();
    } catch (error: any) {
      console.error('Error deleting wheel:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete question wheel',
        variant: 'destructive',
      });
    }
  };

  const handleEnrichWithKeywords = async (wheelId: string, wheelName: string, questionCount: number) => {
    const confirmed = confirm(
      `Enrich "${wheelName}" with Google Ads keyword data?\n\n` +
      `This will:\n` +
      `- Extract 3-4 keywords from each question\n` +
      `- Fetch search volume and CPC from Google Ads\n` +
      `- Use semantic similarity for better coverage\n` +
      `- Process ${questionCount} questions\n\n` +
      `This may take several minutes. Continue?`
    );

    if (!confirmed) return;

    try {
      setEnrichingWheelId(wheelId);

      toast({
        title: 'Enrichment Started',
        description: `Processing ${questionCount} questions...`,
      });

      const startTime = Date.now();
      const response = await authFetch(
        `/api/sectors/${selectedSectorId}/question-wheels/${wheelId}/enrich-with-keywords`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}), // No limit - process all questions
        }
      );

      const result = await handleResponse<{
        success: boolean;
        enriched: number;
        total: number;
        enrichment_rate: number;
        high_confidence_count: number;
        high_confidence_rate: number;
      }>(response);

      const duration = Math.round((Date.now() - startTime) / 1000);

      toast({
        title: 'Enrichment Complete!',
        description:
          `✅ ${result.enriched}/${result.total} questions enriched (${result.enrichment_rate}%)\n` +
          `🎯 ${result.high_confidence_count} high confidence (${result.high_confidence_rate}%)\n` +
          `⏱️ ${duration}s total`,
      });

      // Reload to show updated data
      await loadTopicsAndWheels();
    } catch (error: any) {
      console.error('Error enriching wheel:', error);
      toast({
        title: 'Enrichment Failed',
        description: error.message || 'Failed to enrich question wheel with Google Ads data',
        variant: 'destructive',
      });
    } finally {
      setEnrichingWheelId(null);
    }
  };

  const selectedSector = sectors.find(s => s.id === parseInt(selectedSectorId));

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Sector Question Wheels</h1>
          <p className="text-muted-foreground">
            Generate question wheels from researched topics
          </p>
        </div>
        <Button onClick={loadTopicsAndWheels} variant="outline" size="sm" disabled={!selectedSectorId}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Sector Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Select Sector</CardTitle>
          <CardDescription>
            Choose a sector to view researched topics and generate question wheels
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label>Sector</Label>
            <Select value={selectedSectorId} onValueChange={setSelectedSectorId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a sector..." />
              </SelectTrigger>
              <SelectContent>
                {sectors.map(sector => (
                  <SelectItem key={sector.id} value={sector.id.toString()}>
                    {sector.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {selectedSectorId && (
        <>
          {/* Researched Topics */}
          <Card>
            <CardHeader>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Researched Topics</CardTitle>
                    <CardDescription>
                      Select topics to include in question wheel
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAll}
                      disabled={topics.length === 0}
                    >
                      {selectedTopicIds.length === topics.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    <Button
                      onClick={handleGenerateWheel}
                      disabled={selectedTopicIds.length === 0 || generating}
                    >
                      {generating ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Generate {selectedTopicIds.length} Wheel{selectedTopicIds.length !== 1 ? 's' : ''}
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Search and Filter Controls */}
                <div className="flex gap-4 items-center">
                  <div className="relative flex-1">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search topics..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="onlyWithoutWheels"
                      checked={onlyWithoutWheels}
                      onCheckedChange={(checked) => setOnlyWithoutWheels(!!checked)}
                    />
                    <Label
                      htmlFor="onlyWithoutWheels"
                      className="text-sm font-normal cursor-pointer"
                    >
                      <Filter className="inline h-4 w-4 mr-1" />
                      Only without wheels
                    </Label>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : topics.length === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No researched topics found for this sector. Topics need to have research completed before generating question wheels.
                  </AlertDescription>
                </Alert>
              ) : (
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={selectedTopicIds.length === topics.length}
                            onCheckedChange={handleSelectAll}
                          />
                        </TableHead>
                        <TableHead>Topic</TableHead>
                        <TableHead>Language</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {topics.map(topic => (
                        <TableRow key={topic.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedTopicIds.includes(topic.id)}
                              onCheckedChange={() => handleTopicToggle(topic.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{topic.topic_name}</span>
                                {topic.has_question_wheel && (
                                  <Badge variant="secondary" className="text-xs">
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    Has Wheel
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground line-clamp-1">
                                {topic.topic_description}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{topic.language || 'es'}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(topic.created_at).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* Generated Wheels */}
          <Card>
            <CardHeader>
              <CardTitle>Generated Question Wheels</CardTitle>
              <CardDescription>
                View and manage question wheels for {selectedSector?.name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : wheels.length === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No question wheels generated yet. Select topics above and click "Generate Wheel" to create one.
                  </AlertDescription>
                </Alert>
              ) : (
                <ScrollArea className="h-[300px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Wheel Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Questions</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {wheels.map(wheel => (
                        <TableRow key={wheel.id}>
                          <TableCell>
                            <div className="font-medium">{wheel.name}</div>
                            {wheel.description && (
                              <div className="text-xs text-muted-foreground line-clamp-1">
                                {wheel.description}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{wheel.wheel_type}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <MessageSquare className="h-4 w-4" />
                              {wheel.question_count}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={wheel.status === 'published' ? 'default' : 'secondary'}
                            >
                              {wheel.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(wheel.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewWheel(wheel)}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View
                              </Button>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleEnrichWithKeywords(wheel.id, wheel.name, wheel.question_count)}
                                disabled={enrichingWheelId === wheel.id}
                              >
                                {enrichingWheelId === wheel.id ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                    Enriching...
                                  </>
                                ) : (
                                  <>
                                    <TrendingUp className="h-4 w-4 mr-1" />
                                    Enrich
                                  </>
                                )}
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteWheel(wheel.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* View Wheel Details Modal */}
      <Dialog open={detailsModal.open} onOpenChange={(open) => setDetailsModal(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              {detailsModal.wheel?.name}
            </DialogTitle>
            <DialogDescription>
              {detailsModal.wheel?.description}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[500px] pr-4">
            {detailsModal.loading ? (
              <div className="space-y-2">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : (
              <div className="space-y-4">
                {detailsModal.questions.map((question, idx) => (
                  <Card key={question.id}>
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">
                          {idx + 1}
                        </div>
                        <div className="flex-1 space-y-2">
                          <div className="font-medium">{question.question_text}</div>
                          <div className="flex gap-2 flex-wrap">
                            <Badge variant="outline">{question.question_type}</Badge>
                            <Badge variant="outline">{question.intent_type}</Badge>
                            {question.target_persona && (
                              <Badge variant="secondary">{question.target_persona}</Badge>
                            )}
                            <Badge variant="secondary">Priority: {question.priority}</Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
